import { IDocumentEditable } from ".";
import { IItem, instanceOfIPasteRequestable, IPasteRequestable, RelationalActionParameter, instanceOfIPasteApplicable, IPasteApplicable } from "../../../domain/document/items";
import { Section } from "../../../domain/document";

export class PasteExecutor {
  constructor(private _document: IDocumentEditable, private _isQuoting: boolean) {
  }

  public executeAll(): void {
    for (let itemSetId = 0; itemSetId < this._document.itemSets.length; ++itemSetId) {
      const itemSet = this._document.itemSets[itemSetId];
      for (const item of itemSet.items) {
        this.execute(item, itemSetId);
      }
    }
  }

  public execute(pasteSource: IItem, itemSetId: number): void {
    if (!instanceOfIPasteRequestable(pasteSource)) { return; }
    if (!pasteSource.pasteDestinations || pasteSource.pasteDestinations.length === 0) { return; }

    for (const destination of pasteSource.pasteDestinations) {
      // 徳大はpageKeyがnullになっているので注意すること
      const section = this._document.sections
        .filter(s => s.itemSetId === itemSetId && (s.pageKey != null) && s.pageKey === destination.pageKey)
        .pop();
      if (section) {
        // まずは指定されたpageKeyのsectionでマッチするオブジェクトが無いかどうかを調べる
        const result = this.executePasteToSectionItem(pasteSource, section, destination);
        if (result) { continue; }
      }
      // pageKeyでマッチするオブジェクトが無い場合は全てのページのマッチするオブジェクトに適用する
      this.executePasteToTarget(pasteSource, itemSetId, destination);
    }
  }

  private executePasteToSectionItem(pasteSource: IPasteRequestable, section: Section, destination: RelationalActionParameter): boolean {
    let result = false;
    for (const privateId of section.itemPrivateIds) {
      const item = this._document.itemSets[section.itemSetId].items[privateId];
      if (!instanceOfIPasteApplicable(item)) { continue; }
      if (pasteSource.privateId === item.privateId) { continue; }
      if (!item.isPasteApplicable(this._isQuoting)) { continue; }
      if (item.isPasteKeyMatch(destination)) {
        const cloned = item.clone() as IPasteApplicable;
        pasteSource.pasteTo(cloned);
        this._document.updateItem(cloned, {
          itemSetId: section.itemSetId,
          reflexive: true,
          isQuoting: this._isQuoting,
        });
        result = true;
      }
    }
    return result;
  }

  private executePasteToTarget(pasteSource: IPasteRequestable, itemSetId: number, destination: RelationalActionParameter): void {
    const items = this._document.itemSets[itemSetId].items;
    for (const item of items) {
      if (!instanceOfIPasteApplicable(item)) { continue; }
      if (pasteSource.privateId === item.privateId) { continue; }
      if (!item.isPasteApplicable(this._isQuoting)) { continue; }
      if (item.isPasteKeyMatch(destination)) {
        const cloned = item.clone() as IPasteApplicable;
        pasteSource.pasteTo(cloned);
        this._document.updateItem(cloned, {
          itemSetId,
          reflexive: true,
          isQuoting: this._isQuoting,
        });
      }
    }
  }

  /**
   * 複数の文書内転記から値が張り付く挙動になっているオブジェクトを抽出します
   */
  public getMultiPastedObjectPrivateIds(itemSetId: number): number[] {
    const multiPastedObjectPrivateIds: number[] = [];
    const pastedObjectPrivateIds: number[] = [];
    for (const item of this._document.itemSets[0].items) {
      if (!instanceOfIPasteRequestable(item)) { continue; }
      if (!item.pasteDestinations || item.pasteDestinations.length === 0) { continue; }

      for (const destination of item.pasteDestinations) {
        const privateId = this.getPasteDestinationPrivateId(itemSetId, destination, item.privateId);
        if (privateId == null) { continue; }
        if (pastedObjectPrivateIds.some(id => id === privateId)) {
          if (!multiPastedObjectPrivateIds.some(id => id === privateId)) {
            multiPastedObjectPrivateIds.push(privateId);
          }
        } else {
          pastedObjectPrivateIds.push(privateId);
        }
      }
    }
    return multiPastedObjectPrivateIds;
  }

  getPasteDestinationPrivateId(itemSetId: number, destination: RelationalActionParameter, ignorePrivateId: number): number {
      // 徳大はpageKeyがnullになっているので注意すること
      const section = this._document.sections
        .filter(s => s.itemSetId === itemSetId && (s.pageKey != null) && s.pageKey === destination.pageKey)
        .pop();
      if (section) {
        // まずは指定されたpageKeyのsectionでマッチするオブジェクトが無いかどうかを調べる
        const sectionItem = this.searchSectionItem(section, destination, ignorePrivateId);
        if (sectionItem) { return sectionItem.privateId; }
      }
      // pageKeyでマッチするオブジェクトが無い場合は全てのページのマッチするオブジェクトから調べる
      const item = this.searchItem(itemSetId, destination, ignorePrivateId);
      return item
        ? item.privateId
        : null;
  }

  searchItem(itemSetId: number, destination: RelationalActionParameter, ignorePrivateId: number): IItem {
    const items = this._document.itemSets[itemSetId].items;
    for (const item of items) {
      if (!instanceOfIPasteApplicable(item)) { continue; }
      if (ignorePrivateId === item.privateId) { continue; }
      if (item.isPasteKeyMatch(destination)) {
        return item;
      }
    }
  }

  searchSectionItem(section: Section, destination: RelationalActionParameter, ignorePrivateId: number): IItem {
    for (const privateId of section.itemPrivateIds) {
      const item = this._document.itemSets[section.itemSetId].items[privateId];
      if (!instanceOfIPasteApplicable(item)) { continue; }
      if (ignorePrivateId === item.privateId) { continue; }
      if (item.isPasteKeyMatch(destination)) {
        return item;
      }
    }
  }
}
